import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LocalStorageService } from "ngx-store";
import { BehaviorSubject, of } from "rxjs";
import { catchError, pluck, switchMap, tap } from "rxjs/operators";
import { FleetPair, FleetPairInput } from "../model/fleet-pairing";
import { Fleet } from "../model/fleet.model";
import { Page } from "../model/pagination";
import { Url } from "../model/users.model";

@Injectable({
  providedIn: 'root'
})
export class FleetPairingService {
  private readonly domain: Url = this.storage.get('url');

  private readonly endpoint = `${this.domain.rest}/tms/fleet-pairs`;

  private _pairsCache: FleetPair[] = [];

  private _pairsCache$ = new BehaviorSubject<FleetPair[]>([]);

  public constructor(
    private client : HttpClient,
    private storage: LocalStorageService
  ) { }

  /**
   * Get all fleet pairs from the server, then store in cache.
   * @returns Fleet pairs fetched.
   */
  public getPairs() {
    return this.getPairsFromServer()
      .pipe(
        tap((fetchedPairs) => {
          this._pairsCache = fetchedPairs;
          this._pairsCache$.next(this._pairsCache);
        })
      );
  }

  /**
   * Retrieve fleet pair resources from the server.
   * @param options.perPage Number of fleet pairs to show per page.
   * @returns Fleet pairs fetched.
   */
  private getPairsFromServer(options: { perPage?: number } = { perPage: -1 }) {
    const params = new HttpParams({
      fromObject: {
        per_page: `${options.perPage}`,
        include: 'fleet'
      }
    });

    return this.client
      .get<Page<FleetPair>>(this.endpoint, { params })
      .pipe(
        pluck('data'),
        catchError(() => {
          console.error('The list of fleet pairs cannot be fetched.');
          return of<FleetPair[]>([]);
        })
      );
  }

  /**
   * Get all paired fleets of a specific fleet from cache, or
   * query the server if unavailable.
   *
   * If there are no results from cache, query the server and
   * store the fetched pair in cache.
   * @param fleet Identifier of the fleet to query its pairs.
   * @returns An array of paired fleets of the given fleet.
   */
  public getPairingsOf(fleet: Fleet) {
    return this._pairsCache$.pipe(
      switchMap((cachedPairs) => {
        const pairInCache = cachedPairs.find((cachedPair) => cachedPair.fleet_id === fleet.id);

        return pairInCache
          ? of(pairInCache)
          : this.fetchPairingOf(fleet).pipe(
              tap((fetchedPair) => {
                if(fetchedPair) {
                  const toUpdate = this._pairsCache.findIndex((cachedPair) => cachedPair.fleet_id === fetchedPair.fleet_id);

                  toUpdate === -1
                    ? this._pairsCache.push(fetchedPair)
                    : this._pairsCache.splice(toUpdate, 1, fetchedPair);

                  this._pairsCache$.next(this._pairsCache);
                }
              })
            );
      })
    );
  }

  /**
   * Save a fleet pair, and update the cached pairing list.
   * @param pair Fleet-pair to save.
   * @returns Pair saved.
   */
  public pair(pair: FleetPairInput) {
    const identical = this._pairsCache.find((cachedPair) => cachedPair.fleet_id === pair.fleet_id && JSON.stringify(cachedPair.pairs) === JSON.stringify(pair.pairs));

    if(identical) {
      console.info('Fleet pairing is already up to date. No new pairing is made.');
      return of(identical);
    }
    else {
      return this.pairFromServer(pair).pipe(
        tap((savedPair) => {
          if(savedPair) {
            const toUpdate = this._pairsCache.findIndex((cachedPair) => cachedPair.fleet_id === savedPair.fleet_id);

            if(toUpdate !== -1) {
              this._pairsCache.splice(toUpdate, 1, savedPair);
              console.debug('Cached fleet pair is updated.');
            }
            else {
              this._pairsCache.push(savedPair);
              console.debug('Saved fleet pair is cached.');
            }

            this._pairsCache$.next(this._pairsCache);
          }
          else {
            console.debug('No fleet pair is saved. There\'s nothing to cache.');
          }
        })
      );
    }
  }

  /**
   * Remove fleet pairings of the given fleet.
   * @param fleet Fleet to remove it pairings.
   * @returns The removed fleet pairing resource.
   */
  public unpair(fleet: Fleet) {
    const toRemove = this._pairsCache.find((cachedPair) => cachedPair.fleet_id === fleet.id);

    if(!toRemove) {
      console.warn(`Fleet pairings of fleet ${fleet.id} is not found in cache. No unpairing is done.`);
      return of<null>(null);
    }
    else {
      return this.unpairFromServer(toRemove).pipe(
        tap((deletedPair) => {
          if(deletedPair) {
            const indexToRemove = this._pairsCache.findIndex((cachedPair) => cachedPair.fleet_id === deletedPair.fleet_id);

            if(indexToRemove !== -1) {
              const removed = this._pairsCache.splice(indexToRemove, 1);
              this._pairsCache$.next(this._pairsCache);
              console.debug('Fleet pairing resource is removed from cache.', removed);
            }
            else {
              console.debug('No fleet pairing resource is removed from cache.');
            }
          }
        })
      );
    }
  }

  /**
   * Fetch the fleet pair information from the server.
   * @param fleet Identifier of the fleet to query its pairs.
   * @returns The fleet pair object, if it exists.
   */
  private fetchPairingOf(fleet: Fleet) {
    return this.client
      .get<FleetPair>(`${this.endpoint}/${fleet.id}`, {
        params: { include: 'fleet' }
      })
      .pipe(
        catchError((response: HttpErrorResponse) => {
          if(response.status === 404) {
            console.info(`There are no fleets paired to fleet ${fleet.id}.`);
          }
          else {
            console.error(`Paired fleets of fleet ${fleet.id} cannot be fetched.`);
          }

          return of<null>(null);
        })
      );
  }

  /**
   * Save a fleet pair to the server.
   * @param pair Fleet-pair to save.
   * @returns Pair saved, if successful. None otherwise.
   */
  private pairFromServer(pair: FleetPairInput) {
    return this.client
      .post<FleetPair>(this.endpoint, pair)
      .pipe(
        catchError(() => {
          console.error('Fleet pair cannot be saved.', pair);
          return of<null>(null);
        })
      );
  }

  /**
   * Remove a fleet pairing resource from the server.
   * @param pairing Fleet pairing to remove.
   * @returns The removed pair resource.
   */
  private unpairFromServer(pairing: FleetPair) {
    return this.client
      .delete<FleetPair>(`${this.endpoint}/${pairing.fleet_id}`)
      .pipe(
        catchError((response: HttpErrorResponse) => {
          console.error(
            response.status === 404
              ? 'The fleet pair resource to delete does not exist.'
              : 'An error occurred while trying to delete the fleet pair resource.',
            pairing
          );

          return of<null>(null);
        })
      );
  }
}
